import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { StatusNFeEntradaEnum } from '@shared/enums/repo-dfe/status-nfe-entrada.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { OrquestradorCompraService } from '@shared/services/apis/orquestrador-compras/compra.service';
import { OrigemLancamentoEnum } from '@shared/enums/compras/origem-lancamento.enum';
import { CompraLancamentoFinalizacaoStruct } from '@shared/services/structs/compras/compra-lancamento-finalizacao.struct';
import { CompraNotaFiscalEntradaStruct } from '@shared/services/structs/orquestrador-compras/compra-nota-fiscal-entrada.struct';
import { rotuloNotaFiscal } from '../dialogs/card-edit-dialog/card-edit-dialog.types';

/** Resultado de um efeito (estoque ou financeiro) do lançamento de uma NF. */
export interface LancamentoEfeitoResultado {
  isLancado: boolean;
  /** Motivo da falha, quando `isLancado` é falso. */
  erro: string | null;
  /** Integração feita, mas o registro ou um efeito depois dela falhou (não é desfeita). */
  aviso: string | null;
}

/** Resultado do lançamento de uma NF ao finalizar a compra (D-11); efeito `null` não foi pedido. */
export interface LancamentoNotaResultado {
  idNFeEntrada: number;
  estoque: LancamentoEfeitoResultado | null;
  financeiro: LancamentoEfeitoResultado | null;
}

/** Resultado do lançamento pelo pedido (origem Pedido); efeito `null` não foi pedido. */
export interface LancamentoPedidoResultado {
  idCompra: number;
  estoque: LancamentoEfeitoResultado | null;
  financeiro: LancamentoEfeitoResultado | null;
}

/** Motivo da última tentativa falha de cada efeito; `null` = não falhou na última tentativa (ou não foi tentado). */
export interface UltimoErroLancamento {
  estoque: string | null;
  financeiro: string | null;
}

/**
 * Finalização da compra (spec 2026-10-12-compras-etapa-finalizado, D-11): um único fluxo para o arraste, o grupo da
 * NF, o modal do card, o "Gerar lançamento" do painel e a automação "Mover automaticamente". Falha de lançamento não
 * desfaz o movimento (D-04): o card fica com o lançamento pendente.
 */
@Injectable({ providedIn: 'root' })
export class FinalizacaoCompraFluxoService {
  /**
   * Último erro de lançamento por NF ou pedido, só em memória da sessão (o service é singleton): o painel do card em
   * Finalizado mostra o motivo mesmo depois que o toast some. Chave = `origem-id` (`OrigemLancamentoEnum`).
   */
  private readonly ultimosErros = new Map<string, UltimoErroLancamento>();

  constructor(
    private dialog: MatDialog,
    private alertService: AlertService,
    private notasService: NotaFiscalEntradaService,
    private orquestradorCompraService: OrquestradorCompraService,
  ) {}

  /**
   * Entrada em Finalizado: (1) recusa se alguma NF ativa dos pedidos não está `Lancada` na Central (modal "Abrir na
   * Central de NFs"); pedido sem NF passa; (2) chama `mover`, que é o PUT CompraEtapa (ou /lote) do chamador, com a UI
   * otimista e os backstops dele (justificativa); (3) movido, lança cada efeito pela origem que o PUT devolveu
   * (`lancamentos`): NF = pelas NFs do pedido, uma chamada por NF; Pedido = pelo pedido; nula = não lança. Avisa o
   * resultado por toast, um por NF e um por pedido (`rotulosCompra`: idCompra → rótulo do pedido).
   *
   * `mover` resolve os lançamentos quando o backend moveu e `null` quando recusou ou o usuário desistiu (já avisado,
   * sem desfazer nada). Esta função resolve `false` quando não moveu (validação ou `mover`): o chamador desfaz o
   * otimista. Resolve `true` só depois do lançamento — falha de lançamento não muda o retorno (D-04). Nunca rejeita.
   */
  async finalizar(
    idsCompra: number[],
    mover: () => Promise<CompraLancamentoFinalizacaoStruct[] | null>,
    rotulosCompra?: Map<number, string>,
  ): Promise<boolean> {
    const notas = await this.validarNotasConfirmadas(idsCompra);
    if (!notas) return false;
    const lancamentos = await mover();
    if (!lancamentos) return false;

    // NFs a lançar por efeito: as dos pedidos cuja origem do efeito é a NF.
    const notasEstoque = new Set<number>();
    const notasFinanceiro = new Set<number>();
    for (const lancamento of lancamentos) {
      const idsNFeEntrada = notas.vinculos.filter(v => v.idCompra === lancamento.idCompra).map(v => v.idNFeEntrada);
      if (lancamento.idOrigemLancamentoEstoque === OrigemLancamentoEnum.NotaFiscal) {
        idsNFeEntrada.forEach(id => notasEstoque.add(id));
      }
      if (lancamento.idOrigemLancamentoFinanceiro === OrigemLancamentoEnum.NotaFiscal) {
        idsNFeEntrada.forEach(id => notasFinanceiro.add(id));
      }
    }

    const avisos: {
      rotulo: string | undefined;
      estoque: LancamentoEfeitoResultado | null;
      financeiro: LancamentoEfeitoResultado | null;
    }[] = [];
    for (const idNFeEntrada of new Set([...notasEstoque, ...notasFinanceiro])) {
      const estoque = notasEstoque.has(idNFeEntrada)
        ? await this.lancarEfeito(() => firstValueFrom(this.notasService.lancarEstoque(idNFeEntrada)),
          'Não foi possível lançar o estoque.')
        : null;
      const financeiro = notasFinanceiro.has(idNFeEntrada)
        ? await this.lancarEfeito(() => firstValueFrom(this.notasService.lancarFinanceiro(idNFeEntrada)),
          'Não foi possível lançar o financeiro.')
        : null;
      this.registrarErros(OrigemLancamentoEnum.NotaFiscal, idNFeEntrada, estoque, financeiro);
      avisos.push({ rotulo: notas.rotulos.get(idNFeEntrada), estoque, financeiro });
    }
    for (const lancamento of lancamentos) {
      const efeitos = {
        estoque: lancamento.idOrigemLancamentoEstoque === OrigemLancamentoEnum.Pedido,
        financeiro: lancamento.idOrigemLancamentoFinanceiro === OrigemLancamentoEnum.Pedido,
      };
      if (!efeitos.estoque && !efeitos.financeiro) continue;
      const { estoque, financeiro } = await this.lancarPedido(lancamento.idCompra, efeitos);
      avisos.push({ rotulo: rotulosCompra?.get(lancamento.idCompra), estoque, financeiro });
    }

    for (const { rotulo, estoque, financeiro } of avisos) {
      this.avisarEfeitos(avisos.length > 1 && rotulo ? `${rotulo}: ` : '', estoque, financeiro);
    }
    return true;
  }

  /**
   * Lança pelo pedido (origem Pedido) os efeitos pedidos: estoque e depois financeiro. Efeito não pedido volta
   * `null`. Cada efeito é independente e nunca lança exceção: erro de rede vira `erro` no efeito.
   */
  async lancarPedido(
    idCompra: number,
    efeitos: { estoque: boolean; financeiro: boolean },
  ): Promise<LancamentoPedidoResultado> {
    const estoque = efeitos.estoque
      ? await this.lancarEfeito(() => firstValueFrom(this.orquestradorCompraService.lancarEstoque(idCompra)),
        'Não foi possível lançar o estoque.')
      : null;
    const financeiro = efeitos.financeiro
      ? await this.lancarEfeito(() => firstValueFrom(this.orquestradorCompraService.lancarFinanceiro(idCompra)),
        'Não foi possível lançar o financeiro.')
      : null;
    this.registrarErros(OrigemLancamentoEnum.Pedido, idCompra, estoque, financeiro);
    return { idCompra, estoque, financeiro };
  }

  /**
   * Lança estoque e depois financeiro de cada NF, uma NF por vez, só os efeitos pedidos (por padrão os dois; o
   * painel passa só os de origem NF, para não tentar pela NF um efeito que a compra lança pelo pedido). Cada efeito é
   * independente: falha no estoque não impede tentar o financeiro. Nunca lança exceção: erro de rede vira `erro` no efeito.
   * Pelo Kanban da compra, o orquestrador aceita a permissão Inserir da NF-e ou, para nota vinculada a compra em
   * Finalizado, a permissão Inserir do Processo de Compras.
   */
  async lancarNotas(
    idsNFeEntrada: number[],
    efeitos: { estoque: boolean; financeiro: boolean } = { estoque: true, financeiro: true },
  ): Promise<LancamentoNotaResultado[]> {
    const resultados: LancamentoNotaResultado[] = [];
    for (const idNFeEntrada of [...new Set(idsNFeEntrada)]) {
      const estoque = efeitos.estoque
        ? await this.lancarEfeito(() => firstValueFrom(this.notasService.lancarEstoque(idNFeEntrada)),
          'Não foi possível lançar o estoque.')
        : null;
      const financeiro = efeitos.financeiro
        ? await this.lancarEfeito(() => firstValueFrom(this.notasService.lancarFinanceiro(idNFeEntrada)),
          'Não foi possível lançar o financeiro.')
        : null;
      this.registrarErros(OrigemLancamentoEnum.NotaFiscal, idNFeEntrada, estoque, financeiro);
      resultados.push({ idNFeEntrada, estoque, financeiro });
    }
    return resultados;
  }

  /** Último erro de lançamento da NF (`idNFeEntrada`) ou do pedido (`idCompra`) nesta sessão; `undefined` = nenhum. */
  ultimoErro(origem: OrigemLancamentoEnum, id: number): UltimoErroLancamento | undefined {
    return this.ultimosErros.get(`${origem}-${id}`);
  }

  /** Efeito que falhou grava o motivo, efeito lançado limpa; efeito não tentado (`null`) mantém o registro. */
  private registrarErros(
    origem: OrigemLancamentoEnum,
    id: number,
    estoque: LancamentoEfeitoResultado | null,
    financeiro: LancamentoEfeitoResultado | null,
  ): void {
    const chave = `${origem}-${id}`;
    const registro: UltimoErroLancamento = { ...(this.ultimosErros.get(chave) ?? { estoque: null, financeiro: null }) };
    if (estoque) registro.estoque = estoque.isLancado ? null : estoque.erro;
    if (financeiro) registro.financeiro = financeiro.isLancado ? null : financeiro.erro;
    if (registro.estoque || registro.financeiro) this.ultimosErros.set(chave, registro);
    else this.ultimosErros.delete(chave);
  }

  /** Toast do resultado dos efeitos de uma NF ou de um pedido (com o aviso do orquestrador, se houver); efeito `null` não foi lançado e fica fora da mensagem. */
  private avisarEfeitos(
    prefixo: string,
    estoque: LancamentoEfeitoResultado | null,
    financeiro: LancamentoEfeitoResultado | null,
  ): void {
    if (estoque && financeiro) {
      if (estoque.isLancado && financeiro.isLancado) {
        this.alertService.show('Sucesso', `${prefixo}Estoque e financeiro lançados.`, AlertType.success);
      } else if (estoque.isLancado) {
        this.alertService.show('Atenção', `${prefixo}Estoque lançado; financeiro não lançado: ${financeiro.erro}`, AlertType.warning);
      } else if (financeiro.isLancado) {
        this.alertService.show('Atenção', `${prefixo}Financeiro lançado; estoque não lançado: ${estoque.erro}`, AlertType.warning);
      } else {
        const motivos = estoque.erro === financeiro.erro ? estoque.erro : `${estoque.erro} ${financeiro.erro}`;
        this.alertService.show('Atenção', `${prefixo}Estoque e financeiro não lançados: ${motivos}`, AlertType.warning);
      }
    } else if (estoque) {
      if (estoque.isLancado) {
        this.alertService.show('Sucesso', `${prefixo}Estoque lançado.`, AlertType.success);
      } else {
        this.alertService.show('Atenção', `${prefixo}Estoque não lançado: ${estoque.erro}`, AlertType.warning);
      }
    } else if (financeiro) {
      if (financeiro.isLancado) {
        this.alertService.show('Sucesso', `${prefixo}Financeiro lançado.`, AlertType.success);
      } else {
        this.alertService.show('Atenção', `${prefixo}Financeiro não lançado: ${financeiro.erro}`, AlertType.warning);
      }
    }
    for (const aviso of [estoque?.aviso, financeiro?.aviso]) {
      if (aviso) this.alertService.show('Atenção', `${prefixo}${aviso}`, AlertType.warning);
    }
  }

  /**
   * D-03(d): só entra em Finalizado com a NF confirmada (Lançada) na Central. O Compras não lê a RepoDFe, então a
   * checagem é aqui, pelo contexto do Orquestrador de cada NF ativa dos pedidos — todas, não só a da moldura do quadro.
   * Devolve os vínculos ativos compra × NF e as NFs (idNFeEntrada → rótulo), vazios quando não há NF (pedido sem NF
   * passa); `null` = recusado e já avisado.
   */
  private async validarNotasConfirmadas(
    idsCompra: number[],
  ): Promise<{ vinculos: CompraNotaFiscalEntradaStruct[]; rotulos: Map<number, string> } | null> {
    try {
      const res = await firstValueFrom(this.notasService.notasPorCompras(idsCompra));
      if (res.isError) {
        this.alertService.show('Erro', res.errorDescription || 'Não foi possível consultar as NF-es dos pedidos.', AlertType.error);
        return null;
      }
      const vinculos = res.lista ?? [];
      const idsNFeEntrada = [...new Set(vinculos.map(nota => nota.idNFeEntrada))];
      const contextos = await Promise.all(idsNFeEntrada.map(id => firstValueFrom(this.notasService.contextoVinculoPedido(id))));
      const notas = new Map<number, string>();
      for (const [indice, contexto] of contextos.entries()) {
        const idNFeEntrada = idsNFeEntrada[indice];
        if (contexto.isError) {
          this.alertService.show('Erro', contexto.errorDescription || 'Não foi possível consultar a NF-e na Central de NFs.', AlertType.error);
          return null;
        }
        const rotulo = rotuloNotaFiscal(contexto.nota?.numero, contexto.nota?.serie);
        if (contexto.nota?.idStatusNFeEntrada !== StatusNFeEntradaEnum.Lancada) {
          // Mesmo aviso com "Abrir a nota" do vínculo (VinculoNotaPedidoFluxoService): a NF abre em outra aba.
          this.dialog.open(ConfirmModalComponent, {
            data: {
              title: 'NF-e não confirmada',
              description: `${rotulo} ainda não foi confirmada. Confirme a NF-e na Central de NFs antes de finalizar a compra.`,
              buttonConfirm: 'Abrir na Central de NFs',
              buttonClose: 'Fechar',
            },
          }).afterClosed().subscribe((result) => {
            if (result?.confirm) {
              window.open(`${window.location.origin}/suprimentos/nota-fiscal-entrada/documento/${idNFeEntrada}`, '_blank', 'noopener');
            }
          });
          return null;
        }
        notas.set(idNFeEntrada, rotulo);
      }
      return { vinculos, rotulos: notas };
    } catch {
      this.alertService.show('Erro', 'Não foi possível consultar a NF-e na Central de NFs.', AlertType.error);
      return null;
    }
  }

  private async lancarEfeito(
    chamar: () => Promise<{ isError: boolean; errorDescription?: string | null; avisoDescricao?: string | null }>,
    erroPadrao: string,
  ): Promise<LancamentoEfeitoResultado> {
    try {
      const res = await chamar();
      return res.isError
        ? { isLancado: false, erro: res.errorDescription || erroPadrao, aviso: null }
        : { isLancado: true, erro: null, aviso: res.avisoDescricao || null };
    } catch {
      return { isLancado: false, erro: erroPadrao, aviso: null };
    }
  }
}
